// SN-20071: the name a document is filed under in E-Docs when staff save it there, from what they
// typed in the Save to E-Docs dialog and the name the surface suggested.

/**
 * The longest name E-Docs is given. Box takes 255 characters at most; this leaves room for the
 * "(1)" the server adds when a second file of the same name arrives in a folder.
 */
export const EDOCS_FILE_NAME_MAX_LENGTH = 240;

/** What a file name cannot hold. The server keeps only what follows a slash, for one. */
export const INVALID_FILE_NAME_CHARACTERS = /[\\/:*?"<>|]/g;

/** The characters in INVALID_FILE_NAME_CHARACTERS, as staff read them on the screen. */
export const INVALID_FILE_NAME_CHARACTERS_LABEL = '\\ / : * ? " < > |';

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** ".pdf" for "Jane Doe.pdf"; null for a name with no extension. */
export const extensionOf = (fileName: string) => {
  const match = /(\.[A-Za-z0-9]{1,5})$/.exec(fileName.trim());
  return match ? match[1] : null;
};

/** The name without its extension (repeated, in any case: "Report.PDF.pdf" is "Report"). */
export const withoutExtension = (fileName: string, extension: string | null) =>
  extension
    ? fileName
        .trim()
        .replace(new RegExp(`(${escapeRegExp(extension)})+$`, "i"), "")
        .trim()
    : fileName.trim();

/** A name as a file can hold it: no characters a file name cannot hold, single spaces. */
export const cleanFileNamePart = (name: string) =>
  name.replace(INVALID_FILE_NAME_CHARACTERS, "").replace(/\s+/g, " ").trim();

export const hasInvalidFileNameCharacters = (name: string) =>
  new RegExp(INVALID_FILE_NAME_CHARACTERS.source).test(name);

/**
 * The file name a document is saved to E-Docs under: what staff typed, cleaned for a file name, or
 * the suggested name when they left the field blank (or typed only characters a name cannot hold).
 * The extension is always the document's own, whatever was typed.
 */
export const edocsFileName = ({
  typedName,
  suggestedFileName,
  extension
}: {
  typedName: string;
  suggestedFileName: string;
  extension: string | null;
}) => {
  const room = EDOCS_FILE_NAME_MAX_LENGTH - (extension?.length ?? 0);
  const base =
    cleanFileNamePart(withoutExtension(typedName, extension)) ||
    cleanFileNamePart(withoutExtension(suggestedFileName, extension)) ||
    "Document";
  return `${Array.from(base).slice(0, room).join("").trim()}${extension ?? ""}`;
};

/** The line under the name field: what happens to the name as it is typed now. */
export const describeTypedName = ({
  typedName,
  suggestedFileName,
  extension
}: {
  typedName: string;
  suggestedFileName: string;
  extension: string | null;
}) => {
  if (!typedName.trim()) {
    const fallback = edocsFileName({
      typedName: "",
      suggestedFileName,
      extension
    });
    return `Left blank, it is saved as “${fallback}”.`;
  }
  if (hasInvalidFileNameCharacters(typedName)) {
    return `A file name can't hold ${INVALID_FILE_NAME_CHARACTERS_LABEL}, so they are left out.`;
  }
  return "This is the name you will see in the patient's E-Docs.";
};
